import type { JSX } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { DocType } from "../../domain/entities/doc-type";

export interface DocTypeListProps {
  items: DocType[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
}

export function DocTypeList({
  items,
  selectedId,
  onSelect,
  onNew,
}: DocTypeListProps): JSX.Element {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Document Types</h2>
        <Button size="sm" onClick={onNew}>
          + New doc type
        </Button>
      </div>

      <div className="space-y-2">
        {items.length === 0 && (
          <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
            No document types yet. Create one to get started.
          </p>
        )}
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            aria-pressed={selectedId === item.id}
            className={cn(
              "w-full rounded-lg border p-3 text-left transition-colors hover:bg-secondary",
              selectedId === item.id && "border-primary bg-secondary",
            )}
            onClick={() => onSelect(item.id)}
          >
            <div className="flex items-start justify-between gap-3">
              <span className="text-sm font-medium">{item.displayName}</span>
              <Badge
                variant={
                  item.source === "system_seeded" ? "secondary" : "outline"
                }
              >
                {item.source === "system_seeded" ? "System" : "Custom"}
              </Badge>
            </div>
            <span className="mt-1 block text-xs text-muted-foreground">
              {item.key}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
